
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, X, Mail } from "lucide-react";
import { TestResponse, RecruitmentStatus } from "@/types/test-response";
import { useBulkActions } from "@/hooks/useBulkActions";
import { useInterviewInvitation } from "@/hooks/useInterviewInvitation";
import { useState } from "react";

interface ResponsesBulkBarProps {
  selectedResponses: TestResponse[];
  onClearSelection: () => void;
  testId: string;
}

export function ResponsesBulkBar({ 
  selectedResponses, 
  onClearSelection,
  testId
}: ResponsesBulkBarProps) {
  const [bulkStatus, setBulkStatus] = useState<RecruitmentStatus>('pending');
  const { bulkUpdateStatus, exportToCSV, isUpdating } = useBulkActions();
  const { inviteToInterview, isInviting } = useInterviewInvitation();

  const handleBulkUpdate = async () => {
    if (selectedResponses.length === 0) return;
    
    await bulkUpdateStatus.mutateAsync({
      responseIds: selectedResponses.map(r => r.id),
      status: bulkStatus,
    });
    
    onClearSelection();
  };

  const handleExport = () => {
    exportToCSV(selectedResponses);
  };

  const handleBulkInvite = async () => {
    // Filter for eligible responses (completed - allow re-sending)
    const eligibleResponses = selectedResponses.filter(
      response => response.status === 'completed'
    );

    if (eligibleResponses.length === 0) {
      return;
    }

    await inviteToInterview.mutateAsync({
      responseIds: eligibleResponses.map(r => r.id),
      testId
    });
    
    onClearSelection();
  };

  const eligibleForInvite = selectedResponses.filter(
    response => response.status === 'completed'
  ).length;

  if (selectedResponses.length === 0) return null;

  return (
    <div className="sticky top-0 z-40 bg-blue-50 dark:bg-blue-950/50 border-b px-6 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium">
            {selectedResponses.length} selected
          </span>
          
          <div className="flex items-center gap-2">
            <Select value={bulkStatus} onValueChange={(value) => setBulkStatus(value as RecruitmentStatus)}>
              <SelectTrigger className="w-40 h-8 bg-white dark:bg-slate-900 border z-50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-900 border shadow-md z-50">
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="shortlisted">Shortlisted</SelectItem>
                <SelectItem value="hired">Hired</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              size="sm" 
              onClick={handleBulkUpdate}
              disabled={isUpdating}
            >
              Update Status
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {eligibleForInvite > 0 && (
            <Button 
              variant="default" 
              size="sm" 
              onClick={handleBulkInvite}
              disabled={isInviting}
              className="gap-1"
            >
              <Mail className="h-4 w-4" />
              Send Invitations ({eligibleForInvite})
            </Button>
          )}
          
          {/* CSV Export - temporarily disabled via feature flag */}
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClearSelection}
            className="gap-1"
          >
            <X className="h-4 w-4" />
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
}
